<script lang="ts">
  import { page } from '$app/stores'
  import { STRINGS } from '$lib/constants/strings'
  import { utils } from '$lib/utils'
  import type { Country } from '@bruce/api/common/country'
  import { Button, Dialog, Toggle, type Language } from '@bruce/components'

  export let dismiss: boolean | undefined = undefined
  export let closed = false
  export let country: Country | undefined
  let settingsOpen = false
  let advancedCookies = false
  $: lang = $page.params.lang as Language
  $: localeRoute = utils.getRoute(lang)

  function acceptCookies() {
    utils.setCookieConsent('accept')
    closed = true
  }

  function rejectCookies() {
    utils.setCookieConsent('functional')
    closed = true
  }

  function handleAccept() {
    if (advancedCookies) {
      acceptCookies()
    } else {
      rejectCookies()
    }
  }
</script>

{#if !closed}
  {@const showReject =
    !country || (country && country.code !== 'SE' && country.code !== 'NO')}
  <div class="container">
    <div class="dialog" role="dialog">
      <h3>{STRINGS[lang].COOKIE_DIALOG_TITLE}</h3>
      <p>
        {STRINGS[lang].COOKIE_DIALOG_INFO}
        <a href={localeRoute('/privacy')}
          >{STRINGS[lang].COOKIE_DIALOG_READ_MORE}</a
        >.
      </p>
      {#if dismiss}
        <div class="dismiss">
          <Button icon="ph-x" variant="text" on:click={rejectCookies} rounded />
        </div>
      {/if}
      <section class="buttons">
        <Button
          variant={showReject ? 'text' : 'outlined'}
          rounded
          on:click={() => {
            settingsOpen = true
          }}
        >
          {STRINGS[lang].COOKIE_DIALOG_CUSTOM}
        </Button>
        {#if showReject}
          <Button variant="outlined" rounded on:click={rejectCookies}>
            {STRINGS[lang].COOKIE_DIALOG_REJECT}
          </Button>
        {/if}
        <Button rounded on:click={acceptCookies} variant="flat">
          {STRINGS[lang].COOKIE_DIALOG_ACCEPT}
        </Button>
      </section>
    </div>
  </div>
  <div class="cookie-settings-dialog">
    <Dialog
      visible={settingsOpen}
      on:toggleVisible={(event) => (settingsOpen = event.detail.visible)}
    >
      <h2 slot="title">{STRINGS[lang].COOKIE_DIALOG_TITLE}</h2>
      <section slot="content" class="settings">
        {#each STRINGS[lang].COOKIE_DIALOG_DETAILED_INFO as detailedInfo, index (index)}
          <p>{detailedInfo}</p>
        {/each}
        <section class="toggle">
          <Toggle active={true} disabled>
            {STRINGS[lang].COOKIE_DIALOG_TOGGLE_REQUIRED}
          </Toggle>
        </section>
        <p class="subtitle">
          {STRINGS[lang].COOKIE_DIALOG_SUBTITLE_REQUIRED}
        </p>
        <section class="toggle">
          <Toggle
            active={advancedCookies}
            on:change={() => (advancedCookies = !advancedCookies)}
          >
            {STRINGS[lang].COOKIE_DIALOG_TOGGLE_ADVANCED}
          </Toggle>
        </section>
        <p class="subtitle">
          {STRINGS[lang].COOKIE_DIALOG_SUBTITLE_ADVANCED}
        </p>
      </section>
      <section slot="actions">
        <Button variant="outlined" rounded on:click={rejectCookies}>
          {STRINGS[lang].COOKIE_DIALOG_REJECT}
        </Button>
        <Button variant="flat" rounded on:click={handleAccept}>
          {STRINGS[lang].COOKIE_DIALOG_ACCEPT_SETTINGS}
        </Button>
      </section>
    </Dialog>
  </div>
{/if}

<style lang="scss">
  @use '@bruce/assets/css/breakpoints';

  .container {
    z-index: 16;
    position: fixed;
    display: flex;
    justify-content: center;
    width: 100%;
    bottom: 0;
    left: 0;
    pointer-events: none;

    @media screen and (max-width: breakpoints.$breakpoint-sm) {
      min-height: 40%;
    }

    .dialog {
      position: relative;
      background-color: var(--color-surface);
      color: var(--color-on-surface);
      padding: 1.5em;
      border-radius: 1em;
      margin-bottom: 1em;
      max-width: 800px;
      pointer-events: auto;
      box-shadow: 0 0 18px rgba(var(--color-on-surface-rgb), 0.2);

      @media screen and (max-width: breakpoints.$breakpoint-sm) {
        border-radius: 0;
        margin-bottom: 0;
      }

      .dismiss {
        position: absolute;
        top: 1em;
        right: 1em;
      }

      .buttons {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 1em;

        @media screen and (max-width: breakpoints.$breakpoint-sm) {
          margin-top: 1em;
          flex-direction: column;
          align-items: unset;

          :global(button) {
            width: 100%;
            max-width: 100%;
          }
        }
      }
    }
  }

  :global(html:not([dark-theme])) {
    .cookie-settings-dialog {
      --dialog-background-color: var(--moon-white);
    }
  }

  :global(html[dark-theme]) {
    .settings {
      .toggle {
        filter: brightness(1.4);
      }
    }
  }

  .settings {
    section {
      margin: 1em 0;
    }

    .toggle {
      margin-bottom: 0;
      padding: 1em;
      background-color: var(--color-surface);
      border-radius: 1em;
      box-shadow: 0 1px 11px #00000014;
    }

    .subtitle {
      margin: 0;
      font-size: 13px;
      padding: 8px 16px;
    }
  }
</style>
